import React, { useState, useMemo } from 'react'
import {
  ButtonIcon,
  FontIcon,
  tokens,
  Checkbox
} from '@arvoreeducacao/bonsai-ds'
import formatDegree from 'utils/degree'
import { useTranslation } from 'i18n'

interface HierarchyNode {
  id: number
  name: string
  type: string
  children: HierarchyNode[]
  classes: Array<{
    id: number
    classId: number
    className: string
    degree?: unknown
  }>
}

interface SelectedClassesTreeProps {
  teacherClasses: Array<{
    id: number
    classId: number
    className: string
    degree?: unknown
  }>
  classHierarchies: Record<
    number,
    Array<{ id: number; name: string; type: string }>
  >
  deletingIds: number[]
  onClassRemove: (classId: number) => void
}

const SelectedClassesTree = ({
  teacherClasses,
  classHierarchies,
  deletingIds,
  onClassRemove
}: SelectedClassesTreeProps) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set())
  const { t } = useTranslation()

  const hierarchyTree = useMemo(() => {
    const networks: Record<number, HierarchyNode> = {}

    teacherClasses.forEach(item => {
      const hierarchy = classHierarchies[item.classId] || []

      if (hierarchy.length === 0) {
        return
      }

      const hierarchyWithoutLeafClass = hierarchy.filter(
        e => e.id !== item.classId
      )
      const networkEntity = hierarchyWithoutLeafClass.find(
        e => e.type === 'network'
      )
      const schoolEntity = hierarchyWithoutLeafClass.find(
        e => e.type === 'school'
      )
      const classParentEntity = !schoolEntity
        ? hierarchyWithoutLeafClass.find(
            e => e.type === 'class' && e.id !== item.classId
          )
        : null
      const groupingEntity = schoolEntity || classParentEntity

      if (!networkEntity && !groupingEntity) {
        return
      }

      if (networkEntity) {
        if (!networks[networkEntity.id]) {
          networks[networkEntity.id] = {
            id: networkEntity.id,
            name: networkEntity.name,
            type: networkEntity.type,
            children: [],
            classes: []
          }
        }

        const networkNode = networks[networkEntity.id]

        if (groupingEntity) {
          let groupingNode = networkNode.children.find(
            c => c.id === groupingEntity.id
          )

          if (!groupingNode) {
            groupingNode = {
              id: groupingEntity.id,
              name: groupingEntity.name,
              type: groupingEntity.type,
              children: [],
              classes: []
            }
            networkNode.children.push(groupingNode)
          }

          groupingNode.classes.push(item)
        } else {
          networkNode.classes.push(item)
        }
      } else if (groupingEntity) {
        if (!networks[groupingEntity.id]) {
          networks[groupingEntity.id] = {
            id: groupingEntity.id,
            name: groupingEntity.name,
            type: groupingEntity.type,
            children: [],
            classes: []
          }
        }

        networks[groupingEntity.id].classes.push(item)
      }
    })

    return Object.values(networks)
  }, [teacherClasses, classHierarchies])

  const toggleNode = (nodeId: number) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId)
      } else {
        newSet.add(nodeId)
      }
      return newSet
    })
  }

  const countTotalClasses = (node: HierarchyNode): number => {
    return (
      node.classes.length +
      node.children.reduce((sum, child) => sum + countTotalClasses(child), 0)
    )
  }

  const getIndentPadding = (level: number) => {
    return 12 + level * 24
  }

  const renderNode = (node: HierarchyNode, level: number = 0) => {
    const isExpanded = expandedNodes.has(node.id)
    const hasChildren = node.children.length > 0 || node.classes.length > 0
    const isNetwork = node.type === 'network'
    const isSchool = node.type === 'school' || node.type === 'class'
    const totalClasses = countTotalClasses(node)

    return (
      <div key={node.id}>
        {hasChildren ? (
          <button
            type="button"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: `12px 16px`,
              paddingLeft: `${getIndentPadding(level)}px`,
              cursor: 'pointer',
              backgroundColor: isExpanded
                ? tokens.colors.paleGreyFour
                : 'transparent',
              borderBottom: `1px solid ${tokens.colors.paleGreyFour}`,
              gap: '8px',
              width: '100%',
              border: 'none',
              textAlign: 'left'
            }}
            onClick={() => toggleNode(node.id)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggleNode(node.id)
              }
            }}
          >
            <FontIcon
              iconName={isExpanded ? 'caret-down' : 'caret-right'}
              size={16}
              color={tokens.colors.dark4}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  fontWeight: tokens.fontWeights.bold,
                  fontSize: tokens.fontSize.small,
                  color: tokens.colors.dark5
                }}
              >
                {node.name}
              </span>
            </div>
            {(isNetwork || isSchool) && totalClasses > 0 && (
              <span
                style={{
                  fontSize: tokens.fontSize.xsmall,
                  color: tokens.colors.dark4,
                  whiteSpace: 'nowrap'
                }}
              >
                {totalClasses} {totalClasses === 1 ? 'turma' : 'turmas'}
              </span>
            )}
          </button>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: `12px 16px`,
              paddingLeft: `${getIndentPadding(level)}px`,
              borderBottom: `1px solid ${tokens.colors.paleGreyFour}`,
              gap: '8px'
            }}
          >
            <FontIcon
              iconName={isExpanded ? 'caret-down' : 'caret-right'}
              size={16}
              color={tokens.colors.dark4}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  fontWeight: tokens.fontWeights.bold,
                  fontSize: tokens.fontSize.small,
                  color: tokens.colors.dark5
                }}
              >
                {node.name}
              </span>
            </div>
            {(isNetwork || isSchool) && totalClasses > 0 && (
              <span
                style={{
                  fontSize: tokens.fontSize.xsmall,
                  color: tokens.colors.dark4,
                  whiteSpace: 'nowrap'
                }}
              >
                {totalClasses} {totalClasses === 1 ? 'turma' : 'turmas'}
              </span>
            )}
          </div>
        )}

        {isExpanded && (
          <div>
            {node.children.map(child => renderNode(child, level + 1))}
            {node.classes.map(classItem => (
              <div
                key={classItem.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: `12px 16px`,
                  paddingLeft: `${getIndentPadding(level + 1)}px`,
                  borderBottom: `1px solid ${tokens.colors.paleGreyFour}`,
                  gap: '12px'
                }}
              >
                <Checkbox
                  item={{
                    id: `class-${classItem.classId}`,
                    label: classItem.className
                  }}
                  defaultValue={true}
                  onChange={() => {}}
                  hideText
                />
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minWidth: 0
                  }}
                >
                  <span
                    style={{
                      fontWeight: tokens.fontWeights.bold,
                      fontSize: tokens.fontSize.small,
                      color: tokens.colors.dark5
                    }}
                  >
                    {classItem.className}
                  </span>
                  {classItem.degree && (
                    <span
                      style={{
                        fontSize: tokens.fontSize.xsmall,
                        color: tokens.colors.dark4,
                        marginLeft: '8px'
                      }}
                    >
                      {formatDegree(classItem.degree as string)}
                    </span>
                  )}
                </div>
                <ButtonIcon
                  disabled={deletingIds.includes(classItem.classId)}
                  iconName="trash"
                  aria-label={t('Remover turma')}
                  theme={{ type: 'ghost', size: 'large' }}
                  onClick={e => {
                    e.stopPropagation()
                    onClassRemove(classItem.classId)
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      {hierarchyTree.length > 0 ? (
        hierarchyTree.map(node => renderNode(node))
      ) : (
        <div
          style={{
            padding: '24px',
            textAlign: 'center',
            color: tokens.colors.dark4
          }}
        >
          Nenhuma turma selecionada
        </div>
      )}
    </div>
  )
}

export default SelectedClassesTree
